import { c as _c } from "react/compiler-runtime";
import { getOrgInvestigationsByIdOptions } from '@onefootprint/fetch-dashboard';
import { useQueries } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener, useInterval } from 'usehooks-ts';
import { DASHBOARD_IS_LIVE_HEADER, investigationUrl } from '@/config/constants';
import useBrowserNotifications from '@/hooks/use-browser-notifications';
import useSession from '@/hooks/use-session';
import { useHasPercyChatAccess } from '../../hooks/use-chat-widget-enabled';
import { subscribeResourceArrival } from '../../utils/resource-arrival-bus';
import { errorStatus, runOutcome } from './run-outcome';
import { claimRun, forgetRun, pruneWatchedRuns, syncWatchedRunsFromStorage, useWatchedRuns, WATCHED_RUNS_STORAGE_KEY, watchRun } from './watched-runs';
const RUN_POLL_MS = 10000;
const PRUNE_MS = 60000;
const RunCompletionWatcher = () => {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "floatingChat.notifications"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    notify,
    isEnabled,
    permission
  } = useBrowserNotifications();
  const hasPercyAccess = useHasPercyChatAccess();
  const {
    isLive,
    setIsLive
  } = useSession();
  const router = useRouter();
  const isWatching = hasPercyAccess && isEnabled && permission === "granted";
  const watchedRuns = useWatchedRuns();
  const isLiveRef = useRef(isLive);
  let t1;
  let t2;
  if ($[1] !== isLive) {
    t1 = () => {
      isLiveRef.current = isLive;
    };
    t2 = [isLive];
    $[1] = isLive;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  useEventListener("storage", _temp);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(_temp2, t3);
  useInterval(_temp3, watchedRuns.length > 0 ? PRUNE_MS : null);
  let t4;
  let t5;
  if ($[5] !== isWatching) {
    t4 = () => {
      if (!isWatching) {
        return;
      }
      return subscribeResourceArrival(_temp4);
    };
    t5 = [isWatching];
    $[5] = isWatching;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== isWatching || $[9] !== watchedRuns) {
    t6 = isWatching ? watchedRuns : [];
    $[8] = isWatching;
    $[9] = watchedRuns;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const polled = t6;
  let t7;
  if ($[11] !== isLive || $[12] !== polled) {
    let t8;
    if ($[14] !== isLive) {
      t8 = run => ({
        ...getOrgInvestigationsByIdOptions({
          path: {
            id: run.id
          },
          headers: {
            [DASHBOARD_IS_LIVE_HEADER]: JSON.stringify(run.isLive ?? isLive)
          }
        }),
        refetchInterval: RUN_POLL_MS,
        refetchIntervalInBackground: true,
        retry: false
      });
      $[14] = isLive;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t7 = polled.map(t8);
    $[11] = isLive;
    $[12] = polled;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = {
      queries: t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const results = useQueries(t8);
  let t9;
  if ($[18] !== polled || $[19] !== results) {
    let t10;
    if ($[21] !== polled) {
      t10 = (result, i) => `${polled[i]?.id}:${result.status}:${result.data?.status ?? ""}:${errorStatus(result.error)}`;
      $[21] = polled;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t9 = results.map(t10).join();
    $[18] = polled;
    $[19] = results;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const signature = t9;
  let t10;
  if ($[23] !== notify || $[24] !== polled || $[25] !== results || $[26] !== router || $[27] !== setIsLive || $[28] !== t) {
    t10 = () => {
      results.forEach((result_0, i_0) => {
        const id = polled[i_0]?.id;
        if (!id) {
          return;
        }
        const outcome = runOutcome(result_0);
        if (outcome === "wait") {
          return;
        }
        if (outcome === "forget") {
          forgetRun(id);
          return;
        }
        const claimed = claimRun(id);
        if (!claimed) {
          return;
        }
        const run_0 = result_0.data;
        notify(run_0?.status === "error" ? t(_temp5) : t(_temp6), {
          body: run_0?.title ?? run_0?.agent?.name ?? t(_temp7),
          tag: `percy-run-${id}`,
          onClick: () => {
            (async () => {
              if (claimed.isLive !== undefined && claimed.isLive !== isLiveRef.current) {
                await setIsLive(claimed.isLive);
              }
              await router.push(investigationUrl(id, {
                chatSessionId: claimed.chatSessionId
              }));
            })();
          }
        });
      });
    };
    $[23] = notify;
    $[24] = polled;
    $[25] = results;
    $[26] = router;
    $[27] = setIsLive;
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== signature) {
    t11 = [signature];
    $[30] = signature;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  useEffect(t10, t11);
  return null;
};
export default RunCompletionWatcher;
function _temp(event) {
  if (event.key === WATCHED_RUNS_STORAGE_KEY) {
    syncWatchedRunsFromStorage();
  }
}
function _temp2() {
  syncWatchedRunsFromStorage();
}
function _temp3() {
  pruneWatchedRuns(Date.now());
}
function _temp4(key, kind, origin) {
  if (kind !== "investigation") {
    return;
  }
  watchRun(key, {
    startedAt: Date.now(),
    chatSessionId: origin.chatSessionId,
    isLive: origin.isLive
  });
}
function _temp5($) {
  return $.runErrorTitle;
}
function _temp6($_0) {
  return $_0.runDoneTitle;
}
function _temp7($_1) {
  return $_1.runDoneBody;
}